import React, {useCallback, useMemo, useRef, useState} from 'react';
import {produce} from 'immer';
import get from 'lodash/get';
import set from 'lodash/set';
import type {GetValueById, SetValue, UseValueById} from 'schema/form/types';
import type {DataFormConfiguration} from 'schema/types';
import type {Data, DataValue, Schema} from 'schemaDefinition/types';
import {Card, Spacer, Text} from 'components';
import {DataForm} from 'schema/form/components/DataForm';
import {
  bookSchema,
  editConfiguration,
  errorBookSchema,
  sampleBook,
  warningBookSchema,
} from './demoSchema';

/**
 * Wires `sampleBook` into a local data store and exposes it through the
 * get/use/set value hooks that `DataForm` expects. The store is a single
 * `dataRef`; `setEditValue` immutably updates it (via immer) and bumps a
 * `timestamp`, whose identity change re-runs `useEditValue` in the form so
 * edited fields re-render. The current value is returned alongside the props
 * so the scene can show it as JSON.
 */
const useDemoDataForm = (
  id: string,
  schema: Schema,
  configuration: Partial<DataFormConfiguration>,
  showErrors?: boolean,
) => {
  const dataRef = useRef<Data>(sampleBook);
  const [data, setData] = useState<Data>(sampleBook);

  const getEditValue = useCallback<GetValueById>(
    valuePath =>
      valuePath === undefined
        ? undefined
        : valuePath === ''
          ? dataRef.current
          : get(dataRef.current, valuePath),
    [],
  );

  const useEditValue = useCallback<UseValueById>(
    valuePath =>
      valuePath === undefined
        ? undefined
        : valuePath === ''
          ? dataRef.current
          : get(dataRef.current, valuePath),
    // `data` is unused in the body, but its identity changes on every edit so
    // the hook re-runs and edited fields re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const setEditValue = useCallback<SetValue>((valuePath, value: DataValue) => {
    dataRef.current = produce(dataRef.current, draft =>
      set(draft, valuePath, value),
    );
    setData(dataRef.current);
  }, []);

  const props = useMemo(
    () => ({
      id,
      mode: 'edit' as const,
      schema,
      relatedData: {},
      configuration,
      showErrors,
      getEditValue,
      useEditValue,
      setEditValue,
    }),
    [
      id,
      schema,
      configuration,
      showErrors,
      getEditValue,
      useEditValue,
      setEditValue,
    ],
  );

  return {props, data};
};

/**
 * Edit mode: `DataForm` renders an editable form from `bookSchema` and keeps
 * the edited entity in local state (see `useDemoDataForm`). The JSON of the
 * current value is shown below the form so the binding between schema and data
 * is visible.
 *
 * Two further examples reuse the same `sampleBook` data with schema variants
 * whose validations the data deliberately fails, to show how the form surfaces
 * warnings and errors. They pass `showErrors` so every issue is highlighted
 * immediately, without having to visit each field first.
 */
export const EditPage: React.FC = () => {
  const {props: editProps, data} = useDemoDataForm(
    'schema-demo-edit',
    bookSchema,
    editConfiguration,
  );
  const {props: warningProps} = useDemoDataForm(
    'schema-demo-edit-warnings',
    warningBookSchema,
    editConfiguration,
    true,
  );
  const {props: errorProps} = useDemoDataForm(
    'schema-demo-edit-errors',
    errorBookSchema,
    editConfiguration,
    true,
  );

  return (
    <>
      <Text variant="body1" kind="secondary">
        {'A schema rendered as an editable form with <DataForm />. Every field, ' +
          'its type and validation comes from the schema definition.'}
      </Text>
      <Spacer size={2} />
      <Card level="level3">
        <Card.Header>
          <Text variant="h2" kind="primary">
            {'Edit book'}
          </Text>
        </Card.Header>
        <Card.Content>
          <DataForm {...editProps} />
        </Card.Content>
      </Card>
      <Spacer size={2} />
      <Card level="level3">
        <Card.Header>
          <Text variant="h2" kind="primary">
            {'Current value'}
          </Text>
        </Card.Header>
        <Card.Content>
          <pre style={{margin: 0, whiteSpace: 'pre-wrap'}}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </Card.Content>
      </Card>
      <Spacer size={2} />
      <Text variant="body1" kind="secondary">
        {'The same data, but with a schema where every field has a validation ' +
          'that the value fails. Each part defines a `level: "warning"` ' +
          'validation, so every field is flagged as a warning.'}
      </Text>
      <Spacer size={2} />
      <Card level="level3">
        <Card.Header>
          <Text variant="h2" kind="primary">
            {'All fields warning'}
          </Text>
        </Card.Header>
        <Card.Content>
          <DataForm {...warningProps} />
        </Card.Content>
      </Card>
      <Spacer size={2} />
      <Text variant="body1" kind="secondary">
        {'The same data once more, but every validation uses the default ' +
          '`level: "error"`, so every field is flagged as a validation error.'}
      </Text>
      <Spacer size={2} />
      <Card level="level3">
        <Card.Header>
          <Text variant="h2" kind="primary">
            {'All fields error'}
          </Text>
        </Card.Header>
        <Card.Content>
          <DataForm {...errorProps} />
        </Card.Content>
      </Card>
    </>
  );
};
